import manageDistributionsDialogTemplate from './ManageDistributionsDialog.template.html';

/*******************************************************************************
 * ManageDistributionsDialogProvider */
/**
 * This dialog is used to manage the list of <b>ItemSalesAcct</b> objects for a
 * <b>ProductItem</b>. The user will have the option of choosing if the
 * distribution will be amount or percentage based. There is no mixing of
 * distributions, so all distributions will either be all amount based or
 * percents.
 *
 * This is created as a provider so it can be accessed during the config block.
 * This way we can "inherit" the controller and make changes to this dialog
 * without having to create an entirely new dialog or modifying this one to
 * accomodate changes needed for specific products. We are doing this for ASB
 * where we need to add the GASB information to this dialog.
 *******************************************************************************/
/* @ngInject */ export function ManageDistributionsDialogProvider() {
  this.controller = controller;

  this.$get = [
    '$injector',
    function ($injector) {
      return {
        controller: this.controller,
        show: $injector.invoke(show, null, { $config: this })
      };
    }
  ];
}

/*******************************************************************************
 * controller */
/**
 * Default controller for the manage distributions dialog.
 *******************************************************************************/
/* @ngInject */ function controller(
  $q,
  $scope,
  uiGridConstants,
  $injector,
  Alert,
  BooksData,
  CommonUIGridTemplates,
  SERVER,
  Tools,
  accountList,
  allowAmount,
  distributions,
  itemAmount
) {
  var $ctrl = this;
  var $globals = {};
  var $svcs = { $ctrl: $ctrl, $globals: $globals };

  $ctrl.addDistribution = $injector.invoke(addDistribution, null, $svcs);
  $ctrl.save = $injector.invoke(save, null, $svcs);

  $globals.updateTotals = $injector.invoke(updateTotals, null, $svcs);
  $globals.refresh = $injector.invoke(refresh, null, $svcs);

  Object.defineProperties($ctrl, {
    /*---------------------------------------------------*/
    /* canChangeType - User can change the distribution  */
    /* type (amount/percent) only when distribution      */
    /* list is empty. `allowAmount` must also be `true`. */
    /*---------------------------------------------------*/
    canChangeType: {
      get: function () {
        return !this.hasData && allowAmount;
      }
    },

    /*-------------------------------------*/
    /* difference - Difference between the */
    /* item amount and the total amount.   */
    /*-------------------------------------*/
    difference: {
      get: function () {
        return Tools.roundToPrecision(this.itemAmount - this.total, 2);
      }
    },

    /*--------------------------------------------------*/
    /* disableAmount/Percent - `true` if radio button   */
    /* should be disabled. If the user can't change the */
    /* type, one is enabled and the other is disabled.  */
    /*--------------------------------------------------*/
    disableAmount: {
      get: function () {
        return !this.canChangeType && !this.useAmounts;
      }
    },
    disablePercent: {
      get: function () {
        return !this.canChangeType && this.useAmounts;
      }
    },

    /*--------------------------------------------------*/
    /* hasData - `true` if list of splits is not empty. */
    /*--------------------------------------------------*/
    hasData: {
      get: function () {
        return distributions.length > 0;
      }
    },

    /*----------------------------------------------------*/
    /* useAmounts - `true` if distributing by amount,     */
    /* `false` if by percent (default). Determined by     */
    /* splits list, if not empty, else by user selection. */
    /*----------------------------------------------------*/
    useAmounts: {
      get: function () {
        return (
          (this.hasData && distributions[0].amount > 0) || !!this.amountsChecked
        );
      }
    }
  });

  $ctrl.amountsChecked = $ctrl.useAmounts;
  $ctrl.accountLabel = SERVER.BooksWebConstants.accountLabel;
  $ctrl.dialogTitle = 'Manage {0} Splits'.format($ctrl.accountLabel);
  $ctrl.displayAcctNo = SERVER.BooksWebConstants.showAcctNumber;
  $ctrl.allowAmount = allowAmount;
  $ctrl.itemAmount = itemAmount;
  $ctrl.item = new BooksData.ItemSalesAcct();
  $ctrl.addValidators = {};

  /*-----------------------------------------------------------------------*/
  /* TODO: Refresh the grid when amount/percent selection changes. This is */
  /* TODO: needed because `ng-change` does not work on `<styled-radio>`    */
  /* TODO: component and can be removed when the component is fixed.       */
  /*-----------------------------------------------------------------------*/
  $scope.$watch('$ctrl.useAmounts', function (newval) {
    $ctrl.dgDistributions.api.grid.refresh();
  });

  /*---------------------------------*/
  /* Grid to display account splits. */
  /*---------------------------------*/
  $ctrl.dgDistributions = {
    enableColumnMenus: false,
    enableCellEdit: false,
    enableCellEditOnFocus: true,
    enableColumnResizing: true,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    minRowsToShow: 6,
    maxRowsToShow: 6,
    data: distributions,
    apiReady: $q.defer(),
    onRegisterApi: function (gridApi) {
      this.apiReady.resolve();

      /*-----------------------------------*/
      /* Add properties required in        */
      /* column definitions and templates. */
      /*-----------------------------------*/
      this.api = Object.defineProperties(gridApi, {
        accountInfo: {
          value: function (id) {
            return accountList
              .filter(function (a) {
                return a.accountID == id;
              })
              .shift();
          }
        },
        accountNumber: {
          value: function (id) {
            return this.accountInfo(id).accountNumber;
          }
        },
        accountName: {
          value: function (id) {
            return this.accountInfo(id).accountName;
          }
        },
        showAmount: {
          get: function () {
            return $ctrl.useAmounts;
          }
        }
      });

      /*------------------------------------------------------*/
      /* Call column refresh methods when table is refreshed. */
      /*------------------------------------------------------*/
      gridApi.core.registerColumnsProcessor(function (cols) {
        cols.some(function (col) {
          if (col.colDef.onRefresh) col.colDef.onRefresh.call(col);
        });
        return cols;
      });

      /*--------------------------------*/
      /* Update totals after cell edit. */
      /*--------------------------------*/
      gridApi.edit.on.afterCellEdit($scope, $globals.updateTotals);
    },
    columnDefs: [
      {
        name: 'Number',
        field: 'accountNumber',
        labelFunction: function (acct) {
          return this.grid.api.accountNumber(acct.accountID);
        },
        maxWidth: 125,
        visible: SERVER.BooksWebConstants.showAcctNumber
      },
      {
        name: 'Name',
        displayName: SERVER.BooksWebConstants.accountLabel,
        field: 'accountID',
        labelFunction: function (acct) {
          return this.grid.api.accountName(acct.accountID);
        },
        cellTooltip: true
      },
      {
        name: 'Amount',
        displayName: '$ (amount)',
        field: 'amount',
        cellClass: 'text-right',
        cellFilter: 'number:2',
        enableCellEdit: true,
        editableCellTemplate: CommonUIGridTemplates.CELL_CURRENCY_INPUT,
        currencySymbol: '',
        maxWidth: 125,
        type: 'number',
        onRefresh: function () {
          this.visible = this.grid.api.showAmount;
        }
      },
      {
        name: 'Percent',
        displayName: '(percent) %',
        field: 'percentage',
        cellClass: 'text-right',
        cellFilter: 'percent:2:true',
        enableCellEdit: true,
        editableCellTemplate: CommonUIGridTemplates.CELL_PERCENT_INPUT,
        percentPrecision: 2,
        maxWidth: 125,
        type: 'number',
        onRefresh: function () {
          this.visible = !this.grid.api.showAmount;
        }
      },
      {
        field: 'delete',
        maxWidth: 10,
        cellTemplate: CommonUIGridTemplates.CELL_DELETE_ROW,
        displayName: ' ',
        enableSorting: false
      }
    ]
  };

  /*-------------------------------------------*/
  /* Set up listener for the row delete event. */
  /*-------------------------------------------*/
  $scope.$on(
    CommonUIGridTemplates.EVENT_DELETE_ROW,
    function (event, grid, row) {
      var index = $ctrl.dgDistributions.data.indexOf(row.entity);
      $ctrl.dgDistributions.data.splice(index, 1);
      $globals.refresh();
    }
  );

  /*-------------------------------------------*/
  /* Update the totals when the grid is ready. */
  /*-------------------------------------------*/
  $ctrl.dgDistributions.apiReady.promise.then(function () {
    $globals.refresh();
  });

  /***************************************************************************
   * $ctrl.addDistribution */
  /**
   * Add distribution to the grid.
   ***************************************************************************/
  /* @ngInject */ function addDistribution($ctrl, $globals, BooksData, Alert) {
    return function () {
      /*------------------------*/
      /* Validate disbursement. */
      /*------------------------*/
      if (!$ctrl.addValidators.validate()) return;

      $ctrl.dgDistributions.data.push(new BooksData.ItemSalesAcct($ctrl.item));

      /*-----------------------------------------------*/
      /* Update the display and refresh accounts list. */
      /*-----------------------------------------------*/
      $ctrl.dgDistributions.api.grid.refresh();

      /*-----------------------------------------------------------------*/
      /* Reset the input and clears any alerts and/or validation errors. */
      /*-----------------------------------------------------------------*/
      $ctrl.item = new BooksData.ItemSalesAcct();

      Alert.clear();
      $ctrl.addValidators.reset();

      $globals.refresh();
    };
  }

  /***************************************************************************
   * $globals.refresh */
  /**
   * Refreshes account list to filter out accounts already listed in the splits
   * and update totals.
   ***************************************************************************/
  /* @ngInject */ function refresh($globals, $ctrl) {
    return function () {
      $ctrl.accountDP = accountList.filter(function (a) {
        return !$ctrl.dgDistributions.data.some(function (d) {
          return d.accountID == a.accountID;
        });
      });

      $globals.updateTotals();
    };
  }

  /***************************************************************************
   * $ctrl.save */
  /**
   * Validate the input and resolve the dialog with new list of splits.
   ***************************************************************************/
  /* @ngInject */ function save($ctrl, Alert) {
    return function () {
      Alert.clear();
      $ctrl.addValidators.reset();

      var messages = [];

      /*-----------------------------------------*/
      /* Make sure at least one account entered. */
      /*-----------------------------------------*/
      if ($ctrl.dgDistributions.data.length < 1)
        messages.push('No accounts entered.');

      /*-------------------------------------------*/
      /* Check the amount has been fully disbursed */
      /*-------------------------------------------*/
      if ($ctrl.useAmounts && $ctrl.difference != 0)
        messages.push(
          'The total split amount must match the total item amount.'
        );
      /*-----------------------------------------------*/
      /* Check the percentage has been fully disbursed */
      /*-----------------------------------------------*/ else if (
        !$ctrl.useAmounts &&
        $ctrl.total != 100
      )
        messages.push('The total split percentage must equal 100%.');

      /*-------------------------------*/
      /* Validation failed, show user. */
      /*-------------------------------*/
      if (messages.length) Alert.error(messages.join('\n'), 'Data Errors');
      /*---------------------------------------------------------------*/
      /* Validation complete, normalize the data and close the dialog. */
      /*---------------------------------------------------------------*/ else {
        var salesAccountList = $ctrl.dgDistributions.data.map(function (d) {
          if ($ctrl.useAmounts) d.percentage = 0;
          else d.amount = 0;
          return d;
        });

        $ctrl.$close(salesAccountList);
      }
    };
  }

  /***************************************************************************
   * $globals.updateTotals */
  /**
   * Updates the total of all distributions.
   ***************************************************************************/
  /* @ngInject */ function updateTotals($ctrl, Tools) {
    return function () {
      $ctrl.total = $ctrl.dgDistributions.data.reduce(function (total, dist) {
        return (
          total +
          Tools.roundMoney(
            $ctrl.useAmounts ? dist.amount : dist.percentage * 100
          )
        );
      }, 0);
    };
  }
}

/*******************************************************************************
 * $svc.show */
/**
 * This will display a modal dialog to provide a means to manage distributions.
 *
 * @param  accountList    List of accounts that can be chosen for the disbursements.
 * @param  allowAmount    This property is an indication to allow for split values
 *                        to be based off the amount. This would be set to false
 *                        in the case of an item with a variable amount such as
 *                        a donation.
 * @param  itemAmount     This is the full amount of the item to disburse(split).
 * @param  distributions  Current list of <b>ItemSalesAcct</b> objects.
 *
 * @return                Promise to listen on.
 *******************************************************************************/
/* @ngInject */ function show($uibModal, $config) {
  return function (accountList, allowAmount, itemAmount, distributions) {
    return $uibModal.open({
      animation: true,
      backdrop: 'static',
      controller: $config.controller,
      controllerAs: '$ctrl',
      bindToController: true,
      size: 'lg',
      template: manageDistributionsDialogTemplate,
      resolve: {
        accountList: function () {
          return accountList;
        },
        allowAmount: function () {
          return allowAmount;
        },
        itemAmount: function () {
          return itemAmount;
        },
        distributions: function () {
          return Array.isArray(distributions) ? distributions : [];
        }
      }
    }).result;
  };
}
